import { createContext, useContext, type ReactElement, type ReactNode } from "react";
import type { KeyboardHolds, LayerHandle, LayerOptions } from "../input/router.ts";

/**
 * The input router as the kit reaches it (ADR-061 decision 4, CLIENT.md 7.3): a modal opens a layer, so
 * Escape closes it through the router, and holds the keyboard while it is open, so no key reaches the game
 * behind it. The page that mounts the kit provides its router; a focused text field needs nothing from here,
 * since the router gives it the keyboard on `focusin`.
 */

/**
 * What the kit needs from the router: its layer stack and its keyboard holds.
 */
export interface KitInput extends KeyboardHolds {
  /**
   * Opens a layer on top of the stack, which Escape closes.
   *
   * @param options How the layer hides and which keys beside Escape close it.
   * @returns The layer's handle.
   */
  open(options: LayerOptions): LayerHandle;
}

const Input = createContext<KitInput | null>(null);

/**
 * Makes the page's input router reachable by every kit component inside it.
 *
 * @param props The router and the components.
 * @returns The provider.
 */
export function KitInputProvider({ input, children }: { readonly input: KitInput; readonly children: ReactNode }): ReactElement {
  return <Input.Provider value={input}>{children}</Input.Provider>;
}

/**
 * The page's input router.
 *
 * @returns The router the nearest `KitInputProvider` gives.
 * @throws Error when no provider is above the component, since a modal without the router could not be
 *   closed with Escape and would leave the game its keys.
 */
export function useKitInput(): KitInput {
  const input = useContext(Input);
  if (input === null) {
    throw new Error("a kit component that needs the input router is mounted outside a KitInputProvider");
  }
  return input;
}
